import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ExternalLink, Pencil, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PigmentDialog } from "@/components/PigmentDialog";
import { Switch } from "@/components/ui/switch";

function CiList({ ci, odd, testId }) {
  return (
    <span className="flex flex-wrap gap-1.5" data-testid={testId}>
      {ci.map((c) => {
        const bad = odd.includes(c);
        return (
          <span key={c} data-testid={bad ? `${testId}-unusual-${c}` : undefined}
            className={`rounded-full border px-2.5 py-0.5 text-xs ${bad ? "border-[#8C2A2A]/40 bg-[#F9ECEC] font-semibold text-[#8C2A2A]" : "border-[#E6DBCF] bg-[#FDFBF7] text-[#4A3B32]"}`}>
            CI {c}
          </span>
        );
      })}
    </span>
  );
}

function ReviewRow({ p, onEdit, onToggle }) {
  const done = !!p.ci_reviewed_at;
  const btn = "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors";
  return (
    <div data-testid={`ci-review-row-${p.id}`} className={`grid gap-3 border-b border-[#E6DBCF] px-5 py-4 md:grid-cols-[1.2fr_1.6fr_auto] md:items-center ${done ? "bg-[#F6F1EA]/60" : ""}`}>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#A88B7D]">{[p.brand, p.line].filter(Boolean).join(" · ")}</p>
        <p className="font-serif text-xl font-semibold text-[#2C1810]">{p.shade || "Line-level entry"}</p>
        <p className="text-[10px] text-[#A88B7D]">{[p.ig_code, p.sku && `SKU ${p.sku}`].filter(Boolean).join(" · ")}</p>
      </div>
      <div className="space-y-1.5">
        <CiList ci={p.ci_numbers} odd={p.unusual_ci} testId={`ci-review-ci-${p.id}`} />
        <p className="text-[11px] text-[#6E5549]">{done ? `Confirmed as correct by ${p.ci_reviewed_by} on ${p.ci_reviewed_at.slice(0, 10)}` : `${p.unusual_ci.length} unusual value${p.unusual_ci.length > 1 ? "s" : ""}: standard CI numbers have 5 digits`}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {p.source_url && <a href={p.source_url} target="_blank" rel="noreferrer" data-testid={`ci-review-source-${p.id}`} className="inline-flex items-center gap-1 text-xs text-[#9C6218] hover:underline">Source <ExternalLink size={12} /></a>}
        <button className={`${btn} border-[#E6DBCF] text-[#4A3B32] hover:border-[#C5A059]`} onClick={() => onEdit(p)} data-testid={`ci-review-edit-${p.id}`}><Pencil size={12} /> Edit</button>
        <button className={`${btn} ${done ? "border-[#E6DBCF] text-[#6E5549] hover:border-[#C5A059]" : "border-[#2E6B4F] bg-[#2E6B4F] text-white hover:bg-[#25573f]"}`} onClick={() => onToggle(p)} data-testid={`ci-review-confirm-${p.id}`}>
          {done ? <><RotateCcw size={12} /> Undo</> : <><CheckCircle2 size={12} /> Confirm as correct</>}
        </button>
      </div>
    </div>
  );
}

export function CiReview() {
  const qc = useQueryClient();
  const [showDone, setShowDone] = useState(false);
  const [edit, setEdit] = useState(null);
  const { data = [], isLoading } = useQuery({ queryKey: ["ci-review"], queryFn: async () => (await api.get("/admin/ci-review")).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["ci-review"] }); qc.invalidateQueries({ queryKey: ["pigments"] }); };
  const pending = data.filter((p) => !p.ci_reviewed_at);
  const list = showDone ? data : pending;
  const toggle = async (p) => {
    try {
      await (p.ci_reviewed_at ? api.delete(`/admin/pigments/${p.id}/ci-confirm`) : api.post(`/admin/pigments/${p.id}/ci-confirm`));
      toast.success(p.ci_reviewed_at ? "Moved back to review" : "Confirmed as correct");
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div data-testid="ci-review">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="font-serif text-2xl font-semibold text-[#2C1810]">CI number review</h3>
          <p className="mt-1 max-w-2xl text-sm text-[#6E5549]">Records with CI values that aren't the usual 5-digit format (e.g. 173995). Check each against its source, then fix it or confirm it as correct. Changing a record's CI numbers sends it back for review.</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-[#4A3B32]">
          <Switch checked={showDone} onCheckedChange={setShowDone} data-testid="ci-review-show-confirmed" /> Show confirmed
        </label>
      </div>
      <p className="mt-4 text-xs text-[#A88B7D]" data-testid="ci-review-count">{pending.length} to review · {data.length - pending.length} confirmed</p>
      <div className="mt-3 overflow-hidden rounded-2xl border border-[#E6DBCF] bg-[#FDFBF7]" data-testid="ci-review-list">
        {list.map((p) => <ReviewRow key={p.id} p={p} onEdit={setEdit} onToggle={toggle} />)}
        {!isLoading && !list.length && <p className="p-8 text-sm italic text-[#A88B7D]" data-testid="ci-review-empty">All unusual CI numbers have been reviewed.</p>}
      </div>
      <PigmentDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} pigment={edit} onSaved={refresh} />
    </div>
  );
}
